import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ZS = ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'];

const normalize = (zones) => {
  const out = {};
  for (const k of ZS) {
    const z = zones?.[k] || {};
    out[k] = {
      start: z.start ?? '',
      stop: z.stop ?? ''
    };
  }
  return out;
};

const validate = (zones) => {
  let prevStop = null;
  for (const k of ZS) {
    const start = parseInt(zones[k].start, 10);
    const stop = parseInt(zones[k].stop, 10);
    if (!Number.isFinite(start) || !Number.isFinite(stop)) return `${k}: invalid numbers`;
    if (stop < start) return `${k}: stop must be >= start`;
    if (prevStop != null && start <= prevStop) return `${k}: start must be > previous stop`;
    prevStop = stop;
  }
  return null;
};

export default function HrZonesModal({ isOpen, onClose, zones, onSave, saving }) {
  const { t } = useTranslation();
  const [local, setLocal] = useState(() => normalize(zones));

  // When opening with new zones, refresh local state.
  useMemo(() => {
    if (isOpen) setLocal(normalize(zones));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const error = validate(local);

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white/95 backdrop-blur-md">
          <h3 className="text-xl font-black text-gray-900 tracking-tight">{t('plan.hr_zones')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors">✕</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-500 font-medium bg-indigo-50 p-4 rounded-xl border border-indigo-100 leading-relaxed">
            {t('plan.hr_zones_hint')}
          </div>

          <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-4 bg-gray-50/80 text-[10px] font-extrabold uppercase tracking-wide text-gray-400 border-b border-gray-200">
              <div className="p-3">{t('plan.zone')}</div>
              <div className="p-3 text-right">{t('plan.start_bpm')}</div>
              <div className="p-3 text-right">{t('plan.stop_bpm')}</div>
              <div className="p-3 text-center"></div>
            </div>
            {ZS.map((k) => (
              <div key={k} className="grid grid-cols-4 border-t border-gray-100 items-center hover:bg-gray-50/50 transition-colors">
                <div className="p-3 font-black text-gray-900 text-sm">{k}</div>
                <div className="p-2 text-right">
                  <input
                    type="number"
                    className="w-16 border border-gray-200 rounded-lg p-1.5 text-right bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all font-mono text-sm font-bold text-gray-700"
                    value={local[k].start}
                    onChange={(e) => setLocal((prev) => ({ ...prev, [k]: { ...prev[k], start: e.target.value } }))}
                  />
                </div>
                <div className="p-2 text-right">
                  <input
                    type="number"
                    className="w-16 border border-gray-200 rounded-lg p-1.5 text-right bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all font-mono text-sm font-bold text-gray-700"
                    value={local[k].stop}
                    onChange={(e) => setLocal((prev) => ({ ...prev, [k]: { ...prev[k], stop: e.target.value } }))}
                  />
                </div>
                <div className="p-3 text-center text-xs font-bold text-gray-400 bg-gray-50/30">
                  {(() => {
                    const start = parseInt(local[k].start, 10);
                    const stop = parseInt(local[k].stop, 10);
                    if (Number.isFinite(start) && Number.isFinite(stop) && stop >= start) {
                      return `${stop - start + 1}`;
                    }
                    return '—';
                  })()}
                </div>
              </div>
            ))}
          </div>

          {error ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-4 font-bold flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {error}
            </div>
          ) : null}
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-full transition-colors text-sm uppercase tracking-wide">
            {t('workout.cancel')}
          </button>
          <button
            onClick={() => onSave(local)}
            disabled={Boolean(error) || saving}
            className={`px-6 py-2.5 bg-indigo-600 text-white rounded-full font-bold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:bg-indigo-700 transition-all duration-200 text-sm uppercase tracking-wide ${
              (error || saving) ? 'opacity-50 cursor-not-allowed shadow-none transform-none' : ''
            }`}
          >
            {saving ? t('plan.saving') : t('plan.save_hr_zones')}
          </button>
        </div>
      </div>
    </div>
  );
}


